import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';

// Auth pages
import AdminLogin from './pages/admin/AdminLogin';
import SSOCallback from './pages/admin/SSOCallback';
import LandingPage from './pages/LandingPage';

// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard';
import ArtworkList from './pages/admin/ArtworkList';
import ArtworkDetail from './pages/admin/ArtworkDetail';
import NewArtwork from './pages/admin/NewArtwork';
import UsersPage from './pages/admin/UsersPage';
import SuppliersPage from './pages/admin/SuppliersPage';
import AuditLogPage from './pages/admin/AuditLogPage';

// Supplier pages
import SupplierDashboard from './pages/supplier/SupplierDashboard';
import SupplierArtworkDetail from './pages/supplier/SupplierArtworkDetail';
import SupplierActivate from './pages/supplier/SupplierActivate';
import SupplierLogin from './pages/supplier/SupplierLogin';
import SupplierResetPassword from './pages/supplier/SupplierResetPassword';

const ProtectedUser = ({ children }) => {
  const { isLoggedIn, isUser, loading } = useAuth();
  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh' }}><div className="spinner spinner-dark" style={{width:32,height:32}} /></div>;
  if (!isLoggedIn || !isUser) return <Navigate to="/admin/login" replace />;
  return children;
};

const ProtectedSupplier = ({ children }) => {
  const { isLoggedIn, isSupplier, loading } = useAuth();
  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh' }}><div className="spinner spinner-dark" style={{width:32,height:32}} /></div>;
  if (!isLoggedIn || !isSupplier) return <Navigate to="/supplier/login" replace />;
  return children;
};

const ProtectedAdmin = ({ children }) => {
  const { isAdmin, loading } = useAuth();
  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh' }}><div className="spinner spinner-dark" style={{width:32,height:32}} /></div>;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
};

const AppRoutes = () => {
  const { isSupplier, isLoggedIn } = useAuth();

  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={
        isLoggedIn
          ? (isSupplier ? <Navigate to="/supplier/dashboard" /> : <Navigate to="/dashboard" />)
          : <LandingPage />
      } />
      <Route path="/admin/login" element={
        isLoggedIn && !isSupplier ? <Navigate to="/dashboard" /> : <AdminLogin />
      } />
      <Route path="/auth/callback" element={<SSOCallback />} />
      <Route path="/dashboard" element={<ProtectedUser><AdminDashboard /></ProtectedUser>} />
      <Route path="/artworks" element={<ProtectedUser><ArtworkList /></ProtectedUser>} />
      <Route path="/artworks/new" element={<ProtectedUser><NewArtwork /></ProtectedUser>} />
      <Route path="/artworks/:id" element={<ProtectedUser><ArtworkDetail /></ProtectedUser>} />
      <Route path="/users" element={<ProtectedUser><UsersPage /></ProtectedUser>} />
      <Route path="/suppliers" element={<ProtectedUser><SuppliersPage /></ProtectedUser>} />
      <Route path="/audit-log" element={<ProtectedUser><ProtectedAdmin><AuditLogPage /></ProtectedAdmin></ProtectedUser>} />

      {/* Supplier */}
      <Route path="/supplier/dashboard" element={<ProtectedSupplier><SupplierDashboard /></ProtectedSupplier>} />
      <Route path="/supplier/login" element={<SupplierLogin />} />
      <Route path="/supplier/activate/:slug" element={<SupplierActivate />} />
      <Route path="/supplier/reset-password/:token" element={<SupplierResetPassword />} />
      <Route path="/supplier/artworks/:id" element={<ProtectedSupplier><SupplierArtworkDetail /></ProtectedSupplier>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              fontFamily: 'var(--font-body)',
              fontSize: '0.9rem',
              borderRadius: '10px',
              boxShadow: 'var(--shadow-lg)',
            }
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}
